import isEqual from 'lodash/isEqual'
import { AudienceSettingsSchemaItem } from '../../../../../types/GCB2'

export function forEachAudienceSchemaItem(
    items: AudienceSettingsSchemaItem[] | undefined,
    cb: (item: AudienceSettingsSchemaItem) => void,
) {
    if (!Array.isArray(items)) {
        return
    }
    items.forEach((inner: any) => {
        if (!inner) {
            return
        }
        if (Array.isArray(inner)) {
            forEachAudienceSchemaItem(inner, cb)
            return
        }
        cb(inner)
        if (Array.isArray(inner.content)) {
            forEachAudienceSchemaItem(inner.content, cb)
        }
        if (Array.isArray(inner.block_schema)) {
            forEachAudienceSchemaItem(inner.block_schema, cb)
        }
    })
}

export function getAudienceSchemaItemDefault(item: AudienceSettingsSchemaItem) {
    if (Object.prototype.hasOwnProperty.call(item, 'defaultValue')) {
        return item.defaultValue
    }
    if (item.multiple) {
        return []
    }
    return undefined
}

function isClearedInput(value: any) {
    return value === '' || value === null
}

function normalizeSchemaValue(value: any, item: AudienceSettingsSchemaItem) {
    const def = getAudienceSchemaItemDefault(item)
    if (item.multiple) {
        if (value == null || value === '') {
            return []
        }
        return Array.isArray(value) ? value : [value]
    }
    if (typeof def === 'boolean') {
        if (value === 'true' || value === 1) {
            return true
        }
        if (value === 'false' || value === 0) {
            return false
        }
        if (value == null || value === '') {
            return def
        }
        return value === true
    }
    // Missing key: still at schema default. Cleared input ('', null) is a real change.
    if (value === undefined) {
        return def
    }
    if (typeof def === 'number' || typeof value === 'number') {
        if (isClearedInput(value)) {
            return value
        }
        const n = Number(value)
        return Number.isNaN(n) ? value : n
    }
    return value
}

function schemaValuesEqual(current: any, defaultVal: any) {
    if (isEqual(current, defaultVal)) {
        return true
    }
    if (isClearedInput(current) || isClearedInput(defaultVal)) {
        return current === defaultVal
    }
    if (
        current == null ||
        defaultVal == null ||
        typeof current === 'boolean' ||
        typeof defaultVal === 'boolean' ||
        Array.isArray(current) ||
        Array.isArray(defaultVal)
    ) {
        return false
    }
    const currentNum = Number(current)
    const defaultNum = Number(defaultVal)
    return !Number.isNaN(currentNum) && !Number.isNaN(defaultNum) && currentNum === defaultNum
}

export function isAudienceBlockChanged(
    blockItem: AudienceSettingsSchemaItem,
    audienceValues: Record<string, any> | undefined,
) {
    if (!blockItem || !Array.isArray(blockItem.block_schema)) {
        return false
    }
    const values = audienceValues || {}
    let changed = false
    forEachAudienceSchemaItem(blockItem.block_schema, (inner) => {
        if (!inner.valueField || changed) {
            return
        }
        const current = normalizeSchemaValue(values[inner.valueField], inner)
        const defaultVal = normalizeSchemaValue(getAudienceSchemaItemDefault(inner), inner)
        if (!schemaValuesEqual(current, defaultVal)) {
            changed = true
        }
    })
    return changed
}

export function resetAudienceBlockValues(
    blockItem: AudienceSettingsSchemaItem,
    audienceValues: Record<string, any>,
    setField?: (target: Record<string, any>, key: string, value: any) => void,
) {
    if (!blockItem || !Array.isArray(blockItem.block_schema) || !audienceValues) {
        return
    }
    const assign =
        setField ||
        ((target, key, value) => {
            target[key] = value
        })
    forEachAudienceSchemaItem(blockItem.block_schema, (inner) => {
        if (!inner.valueField) {
            return
        }
        assign(audienceValues, inner.valueField, getAudienceSchemaItemDefault(inner))
    })
}
